import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import TableFilterToolbar from 'components/TableFilterToolbar';
import { Box } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import { buildProvisionedModuleEditPath } from 'router/routesAliases';
import { getProvisionedTrainingModulesList } from 'http/hyperAdminProvisioned';
import { transformTableData } from 'utils';
import getTableConfig from 'pages/TrainingModules/helpers/getTableConfig';

const PAGE_SIZE = 20;
const TRAINING_MODULE_STATUSES = ['active', 'archived'];

const SORT_FIELD_API_MAP = {
  id: 'id',
  name: 'name',
  updatedAt: 'updatedAt',
  code: 'codeSort',
  status: 'statusSort',
};

const ProvisionedModulesList = ({ companyId, returnTab }) => {
  const history = useHistory();
  const { t } = useTranslation('common');
  const tableConfig = useMemo(() => getTableConfig(), []);
  const [page, setPage] = useState(0);
  const [filtering, setFiltering] = useState({});
  const [sorting, setSorting] = useState({ field: 'id', dir: 'desc' });

  const [listState, fetchList] = useAsyncFn(async () => {
    const sortApiField = SORT_FIELD_API_MAP[sorting.field] || sorting.field;
    return getProvisionedTrainingModulesList(companyId, {
      page,
      pageSize: PAGE_SIZE,
      ...(filtering.search ? { search: filtering.search } : {}),
      ...(filtering.status ? { status: filtering.status } : {}),
      ...(sortApiField ? { [sortApiField]: sorting.dir.toUpperCase() } : {}),
    });
  }, [companyId, page, filtering, sorting]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const items = listState.value?.items || [];
  const totalCount = listState.value?.count || 0;
  const [columns, rows] = useMemo(() => transformTableData(tableConfig, items), [tableConfig, items]);

  const openEditPage = useCallback(
    moduleId => {
      localStorage.setItem('companyEditActiveTab', String(returnTab));
      history.push({
        pathname: buildProvisionedModuleEditPath(companyId, moduleId),
        state: { returnTab },
      });
    },
    [companyId, history, returnTab],
  );

  const tableActions = useMemo(
    () => [
      {
        type: 'icon',
        icon: 'edit',
        onClick: event => {
          openEditPage(Number(event.currentTarget.dataset.key));
        },
      },
    ],
    [openEditPage],
  );

  const filters = useMemo(
    () => [
      {
        type: 'search',
        name: 'search',
        label: t('trainingModules.searchLabel'),
        placeholder: t('trainingModules.searchPlaceholder'),
      },
      {
        type: 'singleSelect',
        name: 'status',
        label: t('tablesLabels.status'),
        options: TRAINING_MODULE_STATUSES.map(value => ({
          value,
          label: t(`trainingModules.statuses.${value}`),
        })),
      },
    ],
    [t],
  );

  const handleFilterChange = (name, value) => {
    setPage(0);
    setFiltering(prev => ({ ...prev, [name]: value }));
  };

  const handleRemoveFilter = name => {
    setPage(0);
    setFiltering(prev => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleSorting = (field, dir) => {
    setSorting({ field, dir });
  };

  return (
    <Box>
      <TableFilterToolbar
        filters={filters}
        filtering={filtering}
        onFilterChange={handleFilterChange}
        onRemoveFilter={handleRemoveFilter}
      />
      <ModalBusyContainer busy={listState.loading}>
        <AppTable
          columns={columns}
          data={rows}
          totalCount={totalCount}
          rowsPerPage={PAGE_SIZE}
          onChangePage={setPage}
          onColumnSorting={handleSorting}
          currentPage={page}
          isLazyLoading
          actions={tableActions}
          actionsColumnWidth="80px"
        />
      </ModalBusyContainer>
    </Box>
  );
};

ProvisionedModulesList.propTypes = {
  companyId: PropTypes.number.isRequired,
  returnTab: PropTypes.number.isRequired,
};

export default ProvisionedModulesList;
